import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { useTaskDetail } from '../lib/tasks';
import { ActionDrawer } from './ActionDrawer';
import { CardGridSkeleton } from './CardGridSkeleton';
import { EmptyState } from './EmptyState';
import { QueryBoundary } from './QueryBoundary';
import { TaskDetailBody } from './TaskDetailBody';

// One task, opened from the queue board (#1034). Container — resolves
// `GET /v1/tasks/:id` and hands the projection to its presenter.
//
// The read is NOT redundant with the row that opened it: the row is the queue
// projection (repo + title + status), and the scoped plan it summarizes away —
// problem, postconditions, the run pulse — only exists on the detail route.
//
// `last` survives the `taskId → null` close edge so the exit animation slides
// out a full panel instead of a blanked one (the DigestDrawer / IssueDetailDrawer
// rule), and keeps the query keyed while the drawer fades.
//
// A 404 is an ANSWER, not a failure — the task was cancelled/pruned, or belongs
// to another account (the API never confirms a foreign task exists). It gets its
// own line instead of the boundary's error box, whose Retry would 404 forever:
// the same explicit-404 branch `RepoPendingTab` / `PrPendingDrawer` hold.

export type TaskDetailDrawerProps = {
  /** The selected task; `null` closes the drawer. */
  readonly taskId: string | null;
  readonly token: string | null;
  readonly onClose: () => void;
};

export function TaskDetailDrawer(props: TaskDetailDrawerProps): JSX.Element {
  const [last, setLast] = createSignal<string | null>(null);
  createEffect(() => {
    if (props.taskId !== null) setLast(props.taskId);
  });

  const detail = useTaskDetail(last, props.token);
  const gone = (): boolean =>
    detail.isError && detail.error instanceof ApiError && detail.error.status === 404;

  return (
    <ActionDrawer
      open={props.taskId !== null}
      title={detail.data?.title ?? t('fleet.task_detail.title')}
      onClose={props.onClose}
    >
      <Show when={!gone()} fallback={<EmptyState message={t('fleet.task_detail.missing')} />}>
        <QueryBoundary query={detail} loading={<CardGridSkeleton listClass="task-detail" />}>
          {(task) => <TaskDetailBody task={task} token={props.token} />}
        </QueryBoundary>
      </Show>
    </ActionDrawer>
  );
}
